import React, { useEffect } from 'react';
import ReactLoading from 'react-loading';
import { withRouter, RouteComponentProps } from 'react-router';
import { connect } from 'react-redux';
import { useQuery } from 'react-apollo';
import { graphql, utils, Patient } from 'kb-shared';
import { PatientToDo, Appointment } from 'kb-shared';
import { JourneyType } from 'kb-shared/utilities/myJourney';

import { ReduxState } from '../../redux';
import AppointmentCTA from 'components/Dashboard/AppointmentCTA';
import AppointmentsCarousel from './components/AppointmentsCarousel';
import CalculatorsWidget from 'components/Dashboard/CalculatorsWidget/CalculatorsWidget';
import MedicationsWidget from 'components/Dashboard/MedicationsWidget/MedicationsWidget';
import PeriodTrackerWidget from 'components/Dashboard/PeriodTrackerWidget/PeriodTrackerWidget';
import PregnancyCalculatorWidget from 'components/Dashboard/PregnancyCalculatorWidget';
import ProfileWidget from 'components/Dashboard/ProfileWidget';
import { ResultsWidget } from 'components/Dashboard/ResultsWidget';
import ToDoItems from 'components/ToDoItems/ToDoItems';
import UnreadMessagesCard from './components/UnreadMessagesCard';
import WelcomeHeader from './components/WelcomeHeader';
import { JourneyWidget } from 'components/Dashboard/JourneyWidget';
import BenefitsWidget from 'components/Dashboard/BenefitsWidget';
import { DashboardWidget } from 'components/Card';
import { MESSAGING } from 'config/features';
import { DarkButton } from 'components/Buttons';
import { QUERY_MEMBERSHIP } from 'kb-shared/graphql/queries';
import { QueryMembershipResponse } from 'kb-shared/graphql/queries_types';
import { MY_JOURNEY_QUERY, PATIENT_QUERY } from './graphql/queries';
import { Container, FloatRightLink, DashboardContent } from './styled';
import CareTeamWidget from 'components/Dashboard/CareTeamWidget';

const { inFuture, isAMH } = utils;
const { getConsentSearch, getConsent, setConsent, setConsentSearch } = utils;

export type PatientAppointments = {
  appointments: Array<Appointment>;
};

type PatientResponse = {
  patient: {
    id: string;
    patientMembership: {
      id: string;
      createdAt: string;
      employer: boolean;
      membershipRules: Array<{
        id: string;
        code: string;
        discountId: string;
      }>;
    };
  };
};

interface ResultValue {
  date: string;
}

interface ResultType {
  afc: ResultValue[];
  labs: ResultValue[];
}

type JourneyResponse = {
  journeyType: JourneyType;
  appointments: Appointment[];
  results: ResultType;
  patientTodos: PatientToDo[];
  cyclePlan: {
    id: string;
    finished?: string;
  };
};

interface Props extends RouteComponentProps {
  patient: Patient;
  messagingFeatureEnabled?: string | boolean;
}

function Dashboard(props: Props) {
  useEffect(() => {
    if (document.body && document.documentElement) {
      document.body.scrollTop = document.documentElement.scrollTop = 0;
    }
    // If a user doesn't have a consent id in their cache and
    // has a search we want to clear this out because they've navigated
    // away in the past
    if (getConsentSearch() && !getConsent()) {
      setConsent(null);
      setConsentSearch(null);
    }
  }, []);

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page('Home');
    }
  }, []);

  const { patient, messagingFeatureEnabled, history } = props;
  const { firstName, gender, patientMembership } = patient;
  const name = firstName;

  const { data: patientData } = useQuery<PatientResponse>(PATIENT_QUERY);
  const {
    data: appointmentsData,
    loading: appointmentsLoading,
    error: appointmentsError,
    refetch
  } = useQuery<PatientAppointments>(graphql.query.PATIENT_APPOINTMENTS);
  const patientResponse = patientData?.patient;

  const { data: journeyData, loading: journeyLoading } = useQuery<JourneyResponse>(
    MY_JOURNEY_QUERY
  );
  const journeyType = journeyData?.journeyType;

  let hasScheduledAMH = false;
  const upcomingAppointments =
    (appointmentsData &&
      appointmentsData?.appointments.filter(appt => {
        if (isAMH(appt, process.env.REACT_APP_ENV)) {
          hasScheduledAMH = true;
        }
        if (inFuture(appt.startTime)) {
          return appt;
        }
        return null;
      })) ||
    [];

  const showAppointmentsLoadingSpinner =
    appointmentsError || appointmentsLoading || !appointmentsData || !appointmentsData.appointments;

  const { data: membershipData } = useQuery<QueryMembershipResponse>(QUERY_MEMBERSHIP, {
    skip: !patientMembership || !patientMembership.membershipId,
    variables: { membershipId: patientMembership && patientMembership.membershipId }
  });

  return (
    <>
      <Container>
        {patientMembership &&
          membershipData &&
          membershipData.membership &&
          patientResponse?.patientMembership.employer &&
          membershipData.membership.documentUrl && (
            <FloatRightLink target="_blank" href={membershipData.membership.documentUrl}>
              <DarkButton>View Member Benefit</DarkButton>
            </FloatRightLink>
          )}
        <WelcomeHeader name={name} />
        {messagingFeatureEnabled && <UnreadMessagesCard />}
        <MedicationsWidget />
        {showAppointmentsLoadingSpinner ? (
          <ReactLoading type="spin" />
        ) : (
          <React.Fragment>
            <ToDoItems appointments={upcomingAppointments} gender={gender} />

            <DashboardContent>
              {journeyLoading ? (
                <ReactLoading type="spin" />
              ) : (
                <>
                  {upcomingAppointments.length > 0 ? (
                    <AppointmentsCarousel
                      upcomingAppointments={upcomingAppointments}
                      history={history}
                      refetch={refetch}
                    />
                  ) : (
                    <AppointmentCTA history={history} />
                  )}
                  {membershipData &&
                    membershipData.membership &&
                    membershipData.membership.description &&
                    patientResponse?.patientMembership.employer && (
                      <DashboardWidget>
                        <BenefitsWidget
                          history={history}
                          description={membershipData.membership.description}
                        />
                      </DashboardWidget>
                    )}

                  {!!(journeyData && journeyType) && (
                    <DashboardWidget>
                      <JourneyWidget history={history} myJourney={journeyData} />
                    </DashboardWidget>
                  )}

                  <ResultsWidget
                    hasScheduledAMH={hasScheduledAMH}
                    onViewResults={() => history.push('/results')}
                  />

                  {gender === 'female' && (
                    <DashboardWidget>
                      <CalculatorsWidget />
                    </DashboardWidget>
                  )}

                  {gender === 'female' && (
                    <DashboardWidget>
                      <PeriodTrackerWidget history={history} />
                    </DashboardWidget>
                  )}

                  {gender === 'female' && (
                    <DashboardWidget>
                      <PregnancyCalculatorWidget />
                    </DashboardWidget>
                  )}

                  <DashboardWidget>
                    <ProfileWidget history={history} />
                  </DashboardWidget>

                  <DashboardWidget>
                    <CareTeamWidget />
                  </DashboardWidget>
                </>
              )}
            </DashboardContent>
          </React.Fragment>
        )}
      </Container>
    </>
  );
}

const mapStateToProps = (state: ReduxState) => ({
  patient: state.patient.patient,
  messagingFeatureEnabled: state.features.features[MESSAGING]
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Dashboard));
